import { iframeSandbox } from "./iframeSandbox";

/**
 * Keep-alive for web blocks on a wall: the page is loaded ONCE and survives
 * the loop.
 *
 * ## Why this exists
 *
 * Elfsight - the Instagram widget - meters VIEWS, one per widget load, and
 * switches the widget off for the rest of the month when a plan runs out
 * (free: 200). A template slide is unmounted every time the loop moves on,
 * so an inline `<iframe>` reloaded the page every time the slide came back:
 * a lobby loop of a few minutes burned tens of thousands of views a month per
 * screen. The same reload is also a flash of white and a fresh round of
 * network on every cycle for any other web block.
 *
 * ## How: the frames live OUTSIDE React, and are never moved
 *
 * An iframe reloads when its element is removed OR moved to another parent,
 * so it cannot follow a slide in and out of the React tree. Instead this host
 * owns one fixed, full-screen container appended to `<body>`, and every frame
 * lives in it for its whole life. A web block on a slide renders an empty
 * placeholder and CLAIMS a frame for its address; the host positions the frame
 * over the placeholder and shows it. When the slide leaves, the block RELEASES
 * the frame, which is hidden - still loaded, still running - and the next claim
 * of the same address gets the same element back: no reload, no view.
 *
 * ## Limits, chosen
 *
 * - Frames draw ABOVE the slide, so a block the designer stacked over a web
 *   page is covered on the wall (the CMS preview still draws inline, in order).
 *   Rare in practice; worth it for the view count.
 * - At most {@link MAX_IDLE} hidden frames are kept; the least recently used is
 *   dropped beyond that. A panel has little memory, and a loop that shows more
 *   distinct pages than this is not one where keep-alive could save much.
 * - A timed reload (`refreshMinutes`) is honoured, but only while the frame is
 *   ON SCREEN or at the moment it is claimed - a hidden frame never reloads,
 *   because a reload nobody sees is a view paid for nothing.
 * - Only the wall uses this (`TemplateSlide`'s `keepAliveWeb`). The CMS draws
 *   web blocks inline: its previews come and go and must not leave frames
 *   behind in an operator's browser.
 */

/** Hidden frames kept loaded for their next turn. */
export const MAX_IDLE = 4;

export interface FramePlacement {
  /** On-screen box, in viewport pixels (`getBoundingClientRect`). */
  left: number;
  top: number;
  width: number;
  height: number;
  /**
   * The page's own viewport, in CSS pixels - the region's canvas size divided
   * by its zoom. The frame is laid out at this size and scaled to the box, the
   * same arithmetic the inline renderer uses.
   */
  viewportWidth: number;
  viewportHeight: number;
  /** Behind the frame while the page loads. */
  background?: string;
}

export interface FrameHandle {
  place: (p: FramePlacement) => void;
  release: () => void;
  /** The element, for tests and diagnostics. */
  readonly element: HTMLIFrameElement;
}

interface Frame {
  src: string;
  wrapper: HTMLDivElement;
  iframe: HTMLIFrameElement;
  claimed: boolean;
  loadedAt: number;
  lastUsed: number;
  refreshMs: number;
  timer: ReturnType<typeof setInterval> | null;
}

let container: HTMLDivElement | null = null;
let frames: Frame[] = [];
let clock: () => number = () => Date.now();

function root(): HTMLDivElement {
  if (container && container.isConnected) return container;
  container = document.createElement("div");
  container.setAttribute("data-testid", "web-frame-host");
  Object.assign(container.style, {
    position: "fixed",
    inset: "0",
    pointerEvents: "none",
    zIndex: "1",
    overflow: "hidden",
  });
  document.body.appendChild(container);
  return container;
}

function reload(frame: Frame) {
  // Re-assigning `src` is a fresh load of the same page, without touching the
  // element's place in the DOM.
  frame.iframe.src = frame.src;
  frame.loadedAt = clock();
}

function stopTimer(frame: Frame) {
  if (frame.timer) clearInterval(frame.timer);
  frame.timer = null;
}

function destroy(frame: Frame) {
  stopTimer(frame);
  frame.wrapper.remove();
  frames = frames.filter((f) => f !== frame);
}

function evictIdle() {
  const idle = frames
    .filter((f) => !f.claimed)
    .sort((a, b) => a.lastUsed - b.lastUsed);
  while (idle.length > MAX_IDLE) destroy(idle.shift()!);
}

function create(src: string): Frame {
  const wrapper = document.createElement("div");
  Object.assign(wrapper.style, {
    position: "absolute",
    overflow: "hidden",
    visibility: "hidden",
  });
  const iframe = document.createElement("iframe");
  iframe.title = "";
  iframe.tabIndex = -1;
  iframe.setAttribute("sandbox", iframeSandbox(src));
  // No camera, microphone or location prompts on a lobby screen.
  iframe.setAttribute("allow", "");
  iframe.setAttribute("data-testid", "kept-web-frame");
  Object.assign(iframe.style, {
    border: "0",
    display: "block",
    transformOrigin: "0 0",
    pointerEvents: "none",
  });
  iframe.src = src;
  wrapper.appendChild(iframe);
  root().appendChild(wrapper);
  const frame: Frame = {
    src,
    wrapper,
    iframe,
    claimed: false,
    loadedAt: clock(),
    lastUsed: clock(),
    refreshMs: 0,
    timer: null,
  };
  frames.push(frame);
  return frame;
}

/**
 * Take a frame showing `src` - the same loaded element as last time if there
 * is an idle one, a new one otherwise. Two blocks showing the same address get
 * two frames: a frame is never shared while claimed.
 */
export function claimWebFrame(
  src: string,
  opts: { refreshMinutes?: number } = {}
): FrameHandle {
  let frame = frames.find((f) => !f.claimed && f.src === src);
  if (!frame) frame = create(src);
  frame.claimed = true;
  frame.lastUsed = clock();
  frame.refreshMs = Math.max(0, (opts.refreshMinutes ?? 0) * 60_000);

  const f = frame;
  // A timed reload that fell due while the frame was hidden happens now,
  // as it comes back on screen - and then on the interval while it stays.
  if (f.refreshMs > 0 && clock() - f.loadedAt >= f.refreshMs) reload(f);
  stopTimer(f);
  if (f.refreshMs > 0) {
    f.timer = setInterval(() => reload(f), f.refreshMs);
  }

  let released = false;
  return {
    element: f.iframe,
    place(p) {
      if (released) return;
      Object.assign(f.wrapper.style, {
        left: `${p.left}px`,
        top: `${p.top}px`,
        width: `${p.width}px`,
        height: `${p.height}px`,
        background: p.background ?? "transparent",
        visibility: "visible",
      });
      const vw = Math.max(1, p.viewportWidth);
      const vh = Math.max(1, p.viewportHeight);
      Object.assign(f.iframe.style, {
        width: `${vw}px`,
        height: `${vh}px`,
        transform: `scale(${p.width / vw})`,
      });
    },
    release() {
      if (released) return;
      released = true;
      f.claimed = false;
      f.lastUsed = clock();
      f.wrapper.style.visibility = "hidden";
      stopTimer(f);
      evictIdle();
    },
  };
}

/** Drop every frame. The wall calls this when its surface unmounts. */
export function clearWebFrames(): void {
  for (const f of [...frames]) destroy(f);
  container?.remove();
  container = null;
}

/** Test seam: a controllable clock. */
export function setWebFrameClockForTests(now: (() => number) | null): void {
  clock = now ?? (() => Date.now());
}
